// src/components/documents/DocumentModal.jsx - V3 ENTERPRISE PORTAL
// ═══════════════════════════════════════════════════════════════════════════
// 📝 DOCUMENT MODAL — Airbnb/Zillow/QuintoAndar Grade
// ═══════════════════════════════════════════════════════════════════════════
// 🔥 V3: Portal a document.body — stacking context independiente
// 🔥 V3: Dark mode automático
// 🔥 V2: Mobile first con safe areas + touch-friendly inputs
// 🔥 ULTRA ROBUSTO: Validación completa + drag & drop + feedback instantáneo
// 🔥 ULTRA RÁPIDO: GPU-accelerated animations + lazy file processing
// 🔥 ULTRA EFICIENTE: React.memo + useCallback en todos los handlers
// ═══════════════════════════════════════════════════════════════════════════

import React, { memo, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Upload,
  Loader2,
  CheckCircle2,
  FileText,
  Image,
  File,
  Database,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { usePortal, useBodyScrollLock, useEscapeKey, portalize } from '../../utils/portalUtils';

// ═══════════════════════════════════════════════════════════════════════════
// 📐 CONSTANTES
// ═══════════════════════════════════════════════════════════════════════════

const MAX_TITLE_LENGTH = 100;
const MAX_DESCRIPTION_LENGTH = 500;
const MAX_FILE_SIZE = 10 * 1024 * 1024;

// ═══════════════════════════════════════════════════════════════════════════
// 🎬 ANIMACIONES
// ═══════════════════════════════════════════════════════════════════════════

const OVERLAY_ANIMATION = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.2 },
};

const DESKTOP_PANEL_ANIMATION = {
  initial: { scale: 0.92, y: 20, opacity: 0 },
  animate: { scale: 1, y: 0, opacity: 1 },
  exit: { scale: 0.92, y: 20, opacity: 0 },
  transition: { type: 'spring', stiffness: 400, damping: 30 },
};

const MOBILE_SHEET_ANIMATION = {
  initial: { y: '100%', opacity: 1 },
  animate: { y: 0, opacity: 1 },
  exit: { y: '100%', opacity: 1 },
  transition: { type: 'spring', damping: 30, stiffness: 300 },
};

// ═══════════════════════════════════════════════════════════════════════════
// 🎨 SUB-COMPONENTE: INPUT CON CONTADOR
// ═══════════════════════════════════════════════════════════════════════════

const CharacterCounter = memo(function CharacterCounter({ current, max }) {
  const percentage = (current / max) * 100;
  const isNearLimit = percentage > 80;
  const isAtLimit = percentage >= 100;

  return (
    <div className="flex items-center justify-end gap-1 mt-1">
      <span
        className={`text-[10px] transition-colors duration-200 ${
          isAtLimit
            ? 'text-red-500 font-semibold'
            : isNearLimit
            ? 'text-amber-500'
            : 'text-slate-400 dark:text-slate-500'
        }`}
      >
        {current}/{max}
      </span>
    </div>
  );
});

// ═══════════════════════════════════════════════════════════════════════════
// 🎨 SUB-COMPONENTE: FILE ICON
// ═══════════════════════════════════════════════════════════════════════════

const FileIcon = memo(function FileIcon({ fileType, isMobile }) {
  const iconSize = isMobile ? 24 : 20;

  if (!fileType) {
    return <File size={iconSize} className="text-slate-500 dark:text-slate-400" strokeWidth={1.5} />;
  }

  if (fileType.startsWith('image/')) {
    return <Image size={iconSize} className="text-emerald-500" strokeWidth={1.5} />;
  }

  if (fileType === 'application/pdf') {
    return <FileText size={iconSize} className="text-red-500" strokeWidth={1.5} />;
  }

  return <File size={iconSize} className="text-slate-500 dark:text-slate-400" strokeWidth={1.5} />;
});

// ═══════════════════════════════════════════════════════════════════════════
// 🎨 SUB-COMPONENTE: DROP ZONE CONTENT
// ═══════════════════════════════════════════════════════════════════════════

const DropZoneContent = memo(function DropZoneContent({
  uploading,
  fileName,
  fileType,
  isMobile,
  primaryColor,
  onReplaceFile,
  onBrowseFiles,
}) {
  if (uploading) {
    return (
      <div className="flex flex-col items-center gap-3 py-6">
        <Loader2
          className={`animate-spin ${isMobile ? 'w-8 h-8' : 'w-8 h-8'}`}
          style={{ color: primaryColor }}
        />
        <p className={`text-slate-600 dark:text-slate-300 font-medium ${isMobile ? 'text-sm' : 'text-sm'}`}>
          Processando arquivo...
        </p>
      </div>
    );
  }

  if (fileName) {
    return (
      <div className="space-y-3 py-4">
        <div className="flex items-center justify-center gap-3">
          <FileIcon fileType={fileType} isMobile={isMobile} />
          <span className={`font-medium text-slate-700 dark:text-slate-200 truncate max-w-[200px] ${isMobile ? 'text-sm' : 'text-sm'}`}>
            {fileName}
          </span>
        </div>
        <p className={`text-emerald-600 dark:text-emerald-400 flex items-center justify-center gap-1 font-medium ${isMobile ? 'text-xs' : 'text-xs'}`}>
          <CheckCircle2 size={16} strokeWidth={2} />
          Arquivo carregado com sucesso
        </p>
        <button
          type="button"
          onClick={onReplaceFile}
          className={`text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium transition-colors ${isMobile ? 'text-sm' : 'text-sm'}`}
        >
          Trocar arquivo
        </button>
      </div>
    );
  }

  return (
    <div
      className="cursor-pointer py-6"
      onClick={onBrowseFiles}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onBrowseFiles?.();
        }
      }}
    >
      <Upload
        className={`mx-auto mb-3 text-slate-400 dark:text-slate-500 ${isMobile ? 'w-10 h-10' : 'w-10 h-10'}`}
        strokeWidth={1.5}
      />
      <p className={`text-slate-600 dark:text-slate-300 font-medium ${isMobile ? 'text-base' : 'text-sm'}`}>
        {isMobile ? 'Clique ou arraste o arquivo' : 'Clique ou arraste o arquivo'}
      </p>
      <p className={`text-slate-400 dark:text-slate-500 mt-1 ${isMobile ? 'text-xs' : 'text-xs'}`}>
        PDF, imagens (max. 10MB)
      </p>
      <div className={`inline-flex items-center gap-1 mt-2 px-3 py-1.5 rounded-lg bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 ${isMobile ? 'text-xs' : 'text-xs'}`}>
        <Database size={14} strokeWidth={1.5} />
        Arquivos grandes vão para cloud
      </div>
    </div>
  );
});

// ═══════════════════════════════════════════════════════════════════════════
// 🎨 SUB-COMPONENTE: FORM FIELD
// ═══════════════════════════════════════════════════════════════════════════

const FormField = memo(function FormField({ label, required, children, isMobile }) {
  return (
    <div>
      <label className={`text-slate-700 dark:text-slate-300 font-medium mb-1.5 block ${isMobile ? 'text-sm' : 'text-sm'}`}>
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
});

// ═══════════════════════════════════════════════════════════════════════════
// 📝 COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

const DocumentModal = memo(function DocumentModal({
  isOpen,
  onClose,
  form,
  onFormChange,
  clients,
  properties,
  isMobile,
  uploading,
  onFileChange,
  onSave,
  fileInputRef,
  primaryColor = '#2563eb',
  isEditing = false,
}) {
  // ─── Hooks ──────────────────────────────────────────────────────────
  const { isDark } = useTheme();
  const mounted = usePortal();
  const dropZoneRef = useRef(null);
  const titleInputRef = useRef(null);
  const scrollContainerRef = useRef(null);

  // ✅ Portal hooks
  useBodyScrollLock(isOpen);
  useEscapeKey(isOpen, onClose);

  // ─── Auto-focus al abrir ─────────────────────────────────────────────
  useEffect(() => {
    if (isOpen && titleInputRef.current) {
      const timer = setTimeout(() => {
        titleInputRef.current?.focus();
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // ─── Handlers con useCallback ────────────────────────────────────────
  const handleTitleChange = useCallback(
    (e) => {
      const value = e.target.value.slice(0, MAX_TITLE_LENGTH);
      onFormChange?.({ ...form, title: value });
    },
    [form, onFormChange]
  );

  const handleDescriptionChange = useCallback(
    (e) => {
      const value = e.target.value.slice(0, MAX_DESCRIPTION_LENGTH);
      onFormChange?.({ ...form, description: value });
    },
    [form, onFormChange]
  );

  const handleClientChange = useCallback(
    (e) => onFormChange?.({ ...form, clientId: e.target.value }),
    [form, onFormChange]
  );

  const handlePropertyChange = useCallback(
    (e) => onFormChange?.({ ...form, propertyId: e.target.value }),
    [form, onFormChange]
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      const file = e.dataTransfer?.files?.[0];
      if (file && onFileChange) {
        if (file.size > MAX_FILE_SIZE) {
          console.warn(`Archivo excede el límite de ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
          return;
        }
        onFileChange({ target: { files: [file] } });
      }
    },
    [onFileChange]
  );

  const handleOverlayClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) {
        onClose?.();
      }
    },
    [onClose]
  );

  const handleReplaceFile = useCallback(() => {
    fileInputRef?.current?.click();
  }, [fileInputRef]);

  const handleBrowseFiles = useCallback(() => {
    fileInputRef?.current?.click();
  }, [fileInputRef]);

  const handleSave = useCallback(() => {
    if (!uploading && form?.title?.trim() && form?.base64) {
      onSave?.();
    }
  }, [uploading, form?.title, form?.base64, onSave]);

  // ─── Safe area padding ──────────────────────────────────────────────
  const safeAreaStyle = isMobile
    ? { paddingBottom: 'env(safe-area-inset-bottom, 16px)' }
    : {};

  // ─── Clases dark mode ───────────────────────────────────────────────
  const bgModal = "bg-white dark:bg-slate-900";
  const bgInput = "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-100";
  const borderLight = "border-slate-200 dark:border-slate-700";
  const textDark = "text-slate-900 dark:text-slate-100";
  const dropZoneBase = "border-slate-300 dark:border-slate-600 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-slate-50 dark:hover:bg-slate-800/50";
  const dropZoneUploading = "border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-950/30";
  const dropZoneComplete = "border-emerald-300 dark:border-emerald-600 bg-emerald-50/50 dark:bg-emerald-950/20";

  const dropZoneClasses = `
    border-2 border-dashed rounded-xl text-center transition-all duration-200
    ${isMobile ? 'min-h-[120px] p-4' : 'p-5'}
    ${
      uploading
        ? dropZoneUploading
        : form?.fileName
        ? dropZoneComplete
        : dropZoneBase + ' cursor-pointer'
    }
  `;

  const inputClasses = `
    w-full border rounded-xl ${bgInput}
    focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400
    outline-none transition-all duration-200
    ${isMobile ? 'py-3.5 px-3.5 text-base' : 'p-3'}
  `;

  const selectClasses = `
    w-full border rounded-xl ${bgInput}
    focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400
    outline-none transition-all duration-200 appearance-none
    bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2220%22%20height%3D%2220%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%2394a3b8%22%20stroke-width%3D%222%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')]
    bg-[length:20px_20px] bg-[right_12px_center] bg-no-repeat pr-10
    ${isMobile ? 'py-3.5 px-3.5 text-base' : 'p-3'}
  `;

  const btnCancel = "border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 active:bg-slate-100 dark:active:bg-slate-700";

  if (!isOpen) return null;

  // ═══════════════════════════════════════════════════════════════════════
  // 🎨 RENDER: FORMULARIO COMPARTIDO
  // ═══════════════════════════════════════════════════════════════════════

  const formContent = (
    <div className="space-y-5 mt-5">
      {/* Título */}
      <FormField label="Título" required isMobile={isMobile}>
        <input
          ref={titleInputRef}
          type="text"
          placeholder="Ex: Contrato de locação"
          value={form?.title || ''}
          onChange={handleTitleChange}
          maxLength={MAX_TITLE_LENGTH}
          className={inputClasses}
          style={{ '--tw-ring-color': primaryColor }}
          autoComplete="off"
          spellCheck={false}
        />
        <CharacterCounter current={form?.title?.length || 0} max={MAX_TITLE_LENGTH} />
      </FormField>

      {/* Descripción */}
      <FormField label="Descrição" isMobile={isMobile}>
        <textarea
          placeholder="Descrição do documento (opcional)"
          value={form?.description || ''}
          onChange={handleDescriptionChange}
          maxLength={MAX_DESCRIPTION_LENGTH}
          rows={isMobile ? 3 : 3}
          className={`${inputClasses} resize-none`}
          style={{ '--tw-ring-color': primaryColor }}
          autoComplete="off"
        />
        <CharacterCounter current={form?.description?.length || 0} max={MAX_DESCRIPTION_LENGTH} />
      </FormField>

      {/* Cliente */}
      <FormField label="Cliente" isMobile={isMobile}>
        <select
          value={form?.clientId || ''}
          onChange={handleClientChange}
          className={selectClasses}
          style={{ '--tw-ring-color': primaryColor }}
        >
          <option value="">— Nenhum cliente —</option>
          {(clients || []).map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </FormField>

      {/* Propiedad */}
      <FormField label="Imóvel" isMobile={isMobile}>
        <select
          value={form?.propertyId || ''}
          onChange={handlePropertyChange}
          className={selectClasses}
          style={{ '--tw-ring-color': primaryColor }}
        >
          <option value="">— Nenhum imóvel —</option>
          {(properties || []).map((p) => (
            <option key={p.id} value={p.id}>{p.title}</option>
          ))}
        </select>
      </FormField>

      {/* Upload de archivo */}
      <FormField label="Arquivo" required isMobile={isMobile}>
        <div
          ref={dropZoneRef}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={dropZoneClasses}
          role="region"
          aria-label="Área de upload de arquivo"
        >
          <input
            ref={fileInputRef}
            type="file"
            onChange={onFileChange}
            className="hidden"
            accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx"
            aria-label="Selecionar arquivo"
          />
          <DropZoneContent
            uploading={uploading}
            fileName={form?.fileName}
            fileType={form?.fileType}
            isMobile={isMobile}
            primaryColor={primaryColor}
            onReplaceFile={handleReplaceFile}
            onBrowseFiles={handleBrowseFiles}
          />
        </div>
      </FormField>

      {/* BOTONES */}
      {isMobile ? (
        <div className={`flex flex-col gap-3 pt-5 border-t ${borderLight} sticky bottom-0 ${bgModal} pb-1`}>
          <button
            type="button"
            onClick={handleSave}
            disabled={uploading || !form?.title?.trim() || !form?.base64}
            className="w-full text-white rounded-xl font-semibold transition-all duration-200 active:scale-[0.98] shadow-md py-3.5 text-base disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
            style={{ backgroundColor: primaryColor, '--tw-shadow-color': primaryColor + '40' }}
          >
            {uploading ? 'Enviando...' : isEditing ? 'Atualizar documento' : 'Salvar documento'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className={`w-full rounded-xl font-medium transition-all duration-200 active:scale-[0.98] py-3.5 text-base ${btnCancel}`}
          >
            Cancelar
          </button>
        </div>
      ) : (
        <div className={`flex justify-end gap-2 pt-5 border-t ${borderLight} sticky bottom-0 ${bgModal} pb-1`}>
          <button
            type="button"
            onClick={onClose}
            className={`px-5 py-2.5 rounded-xl font-medium transition-all duration-200 active:scale-95 ${btnCancel}`}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={uploading || !form?.title?.trim() || !form?.base64}
            className="px-6 py-2.5 text-white rounded-xl font-semibold transition-all duration-200 active:scale-95 shadow-md disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
            style={{ backgroundColor: primaryColor, '--tw-shadow-color': primaryColor + '40' }}
          >
            {uploading ? 'Enviando...' : isEditing ? 'Atualizar documento' : 'Salvar documento'}
          </button>
        </div>
      )}
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════
  // 🎨 RENDER PRINCIPAL CON PORTAL
  // ═══════════════════════════════════════════════════════════════════════

  const modalContent = (
    <AnimatePresence mode="wait">
      <motion.div
        {...OVERLAY_ANIMATION}
        className={`fixed inset-0 z-[99999] flex ${
          isMobile
            ? 'items-end bg-black/70 dark:bg-black/85'
            : 'items-center justify-center bg-black/60 dark:bg-black/80 backdrop-blur-sm p-4'
        }`}
        onClick={handleOverlayClick}
        aria-modal="true"
        role="dialog"
        aria-label={isEditing ? 'Editar documento' : 'Novo documento'}
      >
        {isMobile ? (
          // 📱 MOBILE: BOTTOM SHEET NATIVO
          <motion.div
            {...MOBILE_SHEET_ANIMATION}
            className={`w-full ${bgModal} rounded-t-3xl shadow-2xl max-h-[92vh] overflow-hidden flex flex-col`}
            style={{ ...safeAreaStyle, willChange: 'transform', transform: 'translateZ(0)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
              <div className="w-10 h-1.5 bg-gray-300 dark:bg-slate-600 rounded-full" />
            </div>

            <div className={`flex justify-between items-center sticky top-0 ${bgModal} z-10 px-5 py-2 flex-shrink-0`}>
              <h2 className={`text-lg font-bold ${textDark}`}>
                {isEditing ? 'Editar Documento' : 'Novo Documento'}
              </h2>
              <button
                type="button"
                onClick={onClose}
                className="p-2 -m-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors active:scale-95"
                aria-label="Fechar"
              >
                <X size={22} className="text-slate-400 dark:text-slate-500" strokeWidth={1.5} />
              </button>
            </div>

            <div
              ref={scrollContainerRef}
              className="overflow-y-auto px-5 pb-6 flex-1"
              style={{ maxHeight: 'calc(92vh - 80px)', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}
            >
              {formContent}
            </div>
          </motion.div>
        ) : (
          // 🖥️ DESKTOP: MODAL CENTRADO
          <motion.div
            {...DESKTOP_PANEL_ANIMATION}
            className={`${bgModal} rounded-2xl w-full max-w-lg shadow-2xl border ${borderLight} max-h-[90vh] overflow-y-auto p-6`}
            style={{ willChange: 'transform, opacity', transform: 'translateZ(0)', overscrollBehavior: 'contain' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`flex justify-between items-center sticky top-0 ${bgModal} z-10 pb-4 border-b ${borderLight}`}>
              <h2 className={`text-xl font-bold ${textDark}`}>
                {isEditing ? 'Editar Documento' : 'Novo Documento'}
              </h2>
              <button
                type="button"
                onClick={onClose}
                className="p-2 -m-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors active:scale-95"
                aria-label="Fechar"
              >
                <X size={20} className="text-slate-400 dark:text-slate-500" strokeWidth={1.5} />
              </button>
            </div>

            {formContent}
          </motion.div>
        )}
      </motion.div>
    </AnimatePresence>
  );

  return portalize(mounted, modalContent);
});

// ═══════════════════════════════════════════════════════════════════════════
// 📤 EXPORT
// ═══════════════════════════════════════════════════════════════════════════

DocumentModal.displayName = 'DocumentModal';
export default DocumentModal;